import { z } from 'zod';

/**
 * Pedagogical AI tutor (ADR-022): class-scoped, enabled by the teacher, used with the student's consent and, for
 * minors, a linked guardian's approval. Guides with hints and questions; never hands over graded answers.
 */

export const AI_TUTOR_CONSENT_VERSION = 'tutor-2026-10-26';
export const TUTOR_MAX_INPUT_CHARS = 2000;
export const TUTOR_MAX_TURNS_PER_SESSION = 40;
export const TUTOR_RETENTION_DAYS = 30;

/** Why the tutor is not usable in a class right now (the first failing rule). */
export type TutorBlockReason =
  | 'unavailable'
  | 'account'
  | 'class_disabled'
  | 'consent_required'
  | 'guardian_approval_required'
  | 'quiz_in_progress'
  | 'quota';

export const tutorConsentSchema = z.object({ consent: z.boolean() });
export const tutorClassSettingSchema = z.object({ enabled: z.boolean() });
export const tutorGuardianApprovalSchema = z.object({ approved: z.boolean() });
export const createTutorSessionSchema = z.object({ classId: z.uuid(), topic: z.string().trim().max(120).optional() });
export const tutorMessageSchema = z.object({ text: z.string().trim().min(1).max(TUTOR_MAX_INPUT_CHARS) });

export interface TutorClassStatusDto {
  classId: string;
  className: string;
  enabledByTeacher: boolean;
  usable: boolean;
  reason: TutorBlockReason | null;
}

export interface TutorStatusDto {
  /** The platform has the tutor switched on and an AI provider configured. */
  available: boolean;
  consented: boolean;
  consentVersion: string;
  minor: boolean;
  /** Minors: a linked guardian has approved. */
  guardianApproved: boolean;
  dailyLimit: number;
  remainingToday: number;
  classes: TutorClassStatusDto[];
}

export interface TutorMessageDto {
  id: number;
  role: 'student' | 'tutor' | 'safety';
  body: string;
  createdAt: string;
}

export interface TutorSessionDto {
  id: string;
  classId: string;
  topic: string | null;
  createdAt: string;
  lastAt: string;
  turnsLeft: number;
  messages: TutorMessageDto[];
}

export interface TutorReplyDto {
  student: TutorMessageDto;
  reply: TutorMessageDto;
  /** Personal data hidden before anything left the server. */
  redacted: number;
  remainingToday: number;
  turnsLeft: number;
}

/** Teacher view: counts only, never content. */
export interface TutorClassUsageDto {
  enabled: boolean;
  studentsThisWeek: number;
  messagesThisWeek: number;
}

/** Guardian view of one child: approval and a weekly message count, never content. */
export interface GuardianTutorDto {
  approved: boolean;
  messagesThisWeek: number;
}
